import { getAdpApiHeaders } from './adpDataSources.api'
import { config } from '@/config'

// ─── Types ────────────────────────────────────────────────────────────────────

export type PipelineItem = {
    data_flow_id: number
    data_flow_name: string
    description: string | null
    purpose: string
    transformation_definition: any // can be object or null
    pipeline_definition?: unknown
    session_id: string
    status: string
    web_socket_connection_id: string
    step_id: string
    is_active: boolean
    created_datetime_timestamp: string
    last_modified_datetime_timestamp: string
    total_dq_passed_records: any // can be object or null
    total_dq_failed_records: any // can be object or null
    data_flow_type: string
    creator_username_identity: string
    modify_username_identity: string
    target_table_id: number | null
    schedule_type_name: string | null
    is_downloadable: boolean
    folder_name: string
    is_stream_data_flow: boolean
    is_schedule: boolean
    schedule_details?: {
        start_day: number
        start_month: number
        start_year: number
        start_time_hour: number
        start_time_minutes: number
        frequency_hours: number
        frequency_minutes: number
    } | null
}

type PipelineListApiResponse = {
    success: boolean
    status: number
    message: string
    data: {
        result: PipelineItem[]
        searchCount: number
    }
}

export type PipelineListParams = {
    page?: number
    size?: number
    search?: string
    status?: string
}

export type CreateDataFlowBody = {
    data_flow_name: string
    session_id: string
    description: string
    purpose: string
    status: string
    web_socket_connection_id: string
    data_flow_type: string
    validate_source_name: boolean
    source_names: string[]
}

export type DataFlowBootstrapResponse = {
    data_flow_id: number
    data_flow_name: string
    description: string
    session_id: string
}

export type UpdateDataFlowBody = {
    session_id: string
    transformation_definition: string
    pipeline_definition?: string
}

export type DataFlowRecord = PipelineItem & {
    pipeline_definition?: unknown
}

export async function runImmediateEtlJob(
    jobId: string,
    baseUrl = config.adpApiUrl
): Promise<void> {
    const normalizedBase = (typeof baseUrl === 'string' ? baseUrl.trim() : String(baseUrl)).replace(/\/+$/, '')
    if (!normalizedBase) {
        throw new Error('ADP API URL is not configured')
    }
    const trimmedJobId = jobId.trim()
    if (!trimmedJobId) {
        throw new Error('Session ID is required to run the ETL job')
    }

    const url = `${normalizedBase}/etl-jobs/${encodeURIComponent(trimmedJobId)}/run-immediate`
    const res = await fetch(url, {
        method: 'POST',
        headers: getAdpApiHeaders(),
        body: '',
    })
    const json = (await res.json().catch(() => ({}))) as {
        success?: boolean
        message?: string
    }
    if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
        throw new Error(json.message || `Run ETL job failed (${res.status})`)
    }
}

export async function killEtlJob(
    jobId: string,
    baseUrl = config.adpApiUrl
): Promise<void> {
    const normalizedBase = (typeof baseUrl === 'string' ? baseUrl.trim() : String(baseUrl)).replace(/\/+$/, '')
    if (!normalizedBase) {
        throw new Error('ADP API URL is not configured')
    }
    const trimmedJobId = jobId.trim()
    if (!trimmedJobId) {
        throw new Error('Session ID is required to stop the ETL job')
    }

    const url = `${normalizedBase}/etl-jobs/${encodeURIComponent(trimmedJobId)}/kill`
    const res = await fetch(url, {
        method: 'POST',
        headers: getAdpApiHeaders(),
        body: '',
    })
    const json = (await res.json().catch(() => ({}))) as {
        success?: boolean
        message?: string
    }
    if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
        throw new Error(json.message || `Stop ETL job failed (${res.status})`)
    }
}

// ─── API function ─────────────────────────────────────────────────────────────

async function fetchPipelineListUrl(
    url: string,
    params: PipelineListParams = {}
): Promise<{ pipelines: PipelineItem[]; totalCount: number } | null> {
    const queryParams = new URLSearchParams()

    if (params.page !== undefined) queryParams.append('page_number', String(params.page))
    if (params.size !== undefined) queryParams.append('limit', String(params.size))
    if (params.search) queryParams.append('pipeline_name', params.search)
    if (params.status) queryParams.append('filterStatus', params.status)

    const fullUrl = `${url}?${queryParams.toString()}`

    const res = await fetch(fullUrl, {
        method: 'GET',
        headers: getAdpApiHeaders(),
    })

    if (!res.ok) {
        if (res.status === 404) {
            return null
        }
        const json = (await res.json().catch(() => ({}))) as PipelineListApiResponse
        throw new Error(json.message || `Pipeline list request failed (${res.status})`)
    }

    const json = (await res.json().catch(() => ({}))) as PipelineListApiResponse
    if (!json.success || !json.data || !Array.isArray(json.data.result)) {
        throw new Error(json.message || 'Invalid pipeline list response')
    }

    return {
        pipelines: json.data.result,
        totalCount: json.data.searchCount,
    }
}

export async function fetchPipelineList(
    params: PipelineListParams = {},
    baseUrl = config.adpApiUrl
): Promise<{ pipelines: PipelineItem[]; totalCount: number }> {
    const normalizedBase = (typeof baseUrl === 'string' ? baseUrl.trim() : String(baseUrl)).replace(/\/+$/, '')
    if (!normalizedBase) {
        throw new Error('ADP API URL is not configured')
    }

    const url = `${normalizedBase}/data-flows`

    const result = await fetchPipelineListUrl(url, params)
    if (result === null) {
        throw new Error('Pipeline list endpoint not found')
    }

    return result
}

function toDataFlowBootstrapResponse(payload: unknown): DataFlowBootstrapResponse | null {
    if (!payload || typeof payload !== 'object') return null
    const candidate = payload as Record<string, unknown>
    if (
        typeof candidate.data_flow_id !== 'number' ||
        typeof candidate.data_flow_name !== 'string' ||
        typeof candidate.description !== 'string' ||
        typeof candidate.session_id !== 'string'
    ) {
        return null
    }
    return {
        data_flow_id: candidate.data_flow_id,
        data_flow_name: candidate.data_flow_name,
        description: candidate.description,
        session_id: candidate.session_id,
    }
}

export async function createDataFlow(
    body: CreateDataFlowBody,
    baseUrl = config.adpApiUrl
): Promise<DataFlowBootstrapResponse> {
    const normalizedBase = (typeof baseUrl === 'string' ? baseUrl.trim() : String(baseUrl)).replace(/\/+$/, '')
    if (!normalizedBase) {
        throw new Error('ADP API URL is not configured')
    }
    const url = `${normalizedBase}/data-flows`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...getAdpApiHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    })
    const json = (await res.json().catch(() => ({}))) as {
        success?: boolean
        message?: string
        data?: unknown
    }
    if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
        throw new Error(json.message || `Create data flow failed (${res.status})`)
    }

    const created =
        toDataFlowBootstrapResponse(json.data) ??
        toDataFlowBootstrapResponse(json)
    if (!created) {
        throw new Error(
            'Data flow response is missing required fields: data_flow_id, data_flow_name, description, session_id'
        )
    }
    return created;
}

export async function updateDataFlow(
    dataFlowId: number,
    body: UpdateDataFlowBody,
    baseUrl = config.adpApiUrl
): Promise<void> {
    const normalizedBase = (typeof baseUrl === 'string' ? baseUrl.trim() : String(baseUrl)).replace(/\/+$/, '')
    if (!normalizedBase) {
        throw new Error('ADP API URL is not configured')
    }
    const url = `${normalizedBase}/data-flows/${dataFlowId}`
    const res = await fetch(url, {
        method: 'PUT',
        headers: {
            ...getAdpApiHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    })
    const json = (await res.json().catch(() => ({}))) as {
        success?: boolean
        message?: string
    }
    if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
        throw new Error(json.message || `Update data flow failed (${res.status})`)
    }
}

export async function fetchDataFlowRecordById(
    dataFlowId: number,
    baseUrl = config.adpApiUrl
): Promise<DataFlowRecord> {
    const normalizedBase = (typeof baseUrl === 'string' ? baseUrl.trim() : String(baseUrl)).replace(/\/+$/, '')
    if (!normalizedBase) {
        throw new Error('ADP API URL is not configured')
    }
    const url = `${normalizedBase}/records/data-flow/${dataFlowId}`
    const res = await fetch(url, {
        method: 'GET',
        headers: getAdpApiHeaders(),
    })
    const json = (await res.json().catch(() => ({}))) as {
        success?: boolean
        message?: string
        data?: unknown
        result?: unknown
    }
    if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
        throw new Error(json.message || `Fetch data flow record failed (${res.status})`)
    }

    const candidate = (json.data ?? json.result ?? json) as unknown
    if (!candidate || typeof candidate !== 'object') {
        throw new Error('Invalid data flow record response')
    }
    return candidate as DataFlowRecord
}
